type RealtimeErrorPayload = {
  error_type: string;
  message?: string | null;
  stack?: string | null;
  channel_name?: string | null;
  event_name?: string | null;
  user_id?: string | number | null;
  user_email?: string | null;
  page_url?: string | null;
  user_agent?: string | null;
  context?: Record<string, unknown>;
};

const SECRET_KEY_PATTERN = /(authorization|cookie|password|secret|token|api[_-]?key|client[_-]?secret|access[_-]?token|refresh[_-]?token|credential)/i;

function toError(value: unknown): Error {
  if (value instanceof Error) {
    return value;
  }

  if (typeof value === 'string') {
    return new Error(value);
  }

  try {
    return new Error(JSON.stringify(value));
  } catch {
    return new Error(String(value));
  }
}

function scrubString(value: string): string {
  return value
    .replace(/Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi, 'Bearer [REDACTED]')
    .replace(/((?:authorization|password|secret|token|api[_-]?key|client[_-]?secret|access[_-]?token|refresh[_-]?token|credential)=)([^&\s]+)/gi, '$1[REDACTED]');
}

function scrubValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(scrubValue);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [
        key,
        SECRET_KEY_PATTERN.test(key) ? '[REDACTED]' : scrubValue(item),
      ]),
    );
  }

  if (typeof value === 'string') {
    return scrubString(value);
  }

  return value;
}

function resolveAuthUser(nuxtApp: ReturnType<typeof useNuxtApp>) {
  const authUser = (nuxtApp.$auth as any)?.user?.value ?? (nuxtApp.$auth as any)?.user;
  const data = authUser?.data ?? authUser;

  return {
    id: data?.id ?? data?.user?.id ?? null,
    email: data?.email ?? data?.user?.email ?? null,
  };
}

export async function reportRealtimeError(
  nuxtApp: ReturnType<typeof useNuxtApp>,
  errorType: string,
  error: unknown,
  context: {
    channelName?: string | null;
    eventName?: string | null;
    extra?: Record<string, unknown>;
  } = {},
) {
  const normalized = toError(error);
  const authUser = resolveAuthUser(nuxtApp);

  const payload: RealtimeErrorPayload = {
    error_type: errorType,
    message: scrubString(normalized.message || errorType),
    stack: normalized.stack ? scrubString(normalized.stack) : null,
    channel_name: context.channelName ?? null,
    event_name: context.eventName ?? null,
    user_id: authUser.id,
    user_email: authUser.email,
    page_url: typeof window !== 'undefined' ? window.location.href : null,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    context: scrubValue(context.extra ?? {}) as Record<string, unknown>,
  };

  try {
    await $fetch('/api/realtime-errors', {
      method: 'POST',
      body: payload,
      credentials: 'include',
    });
  } catch (reportError) {
    console.warn('[realtime-error-reporter] failed to report realtime error', reportError);
  }
}

export function attachPusherErrorMonitoring(
  nuxtApp: ReturnType<typeof useNuxtApp>,
  pusherClient: any,
  context: {
    userId?: string | number | null;
    userEmail?: string | null;
  } = {},
) {
  const connection = pusherClient?.connection;

  if (!connection?.bind) {
    return;
  }

  connection.bind('error', (error: unknown) => {
    console.error('[notifications/pusher] websocket connection error', error);
    reportRealtimeError(nuxtApp, 'websocket_connection_failure', error, { extra: context });
  });

  connection.bind('unavailable', (error: unknown) => {
    console.error('[notifications/pusher] websocket unavailable', error);
    reportRealtimeError(nuxtApp, 'reconnection_failure', error, { extra: context });
  });

  connection.bind('failed', (error: unknown) => {
    console.error('[notifications/pusher] websocket failed', error);
    reportRealtimeError(nuxtApp, 'websocket_connection_failure', error, { extra: context });
  });

  connection.bind('disconnected', () => {
    console.debug('[notifications/pusher] websocket disconnected');
  });

  connection.bind('state_change', (states: { previous?: string; current?: string }) => {
    if (states?.current === 'unavailable' || states?.current === 'failed') {
      reportRealtimeError(
        nuxtApp,
        'reconnection_failure',
        new Error(`Pusher connection state changed from ${states.previous ?? 'unknown'} to ${states.current}.`),
        { extra: { ...context, previousState: states.previous, currentState: states.current } },
      );
    }
  });
}

export function attachChannelErrorMonitoring(
  nuxtApp: ReturnType<typeof useNuxtApp>,
  channel: any,
  channelName: string,
) {
  const bindable = channel?.subscription ?? channel;

  channel?.error?.((error: unknown) => {
    console.error('[notifications/pusher] channel subscription error', error);
    reportRealtimeError(nuxtApp, 'subscription_failure', error, { channelName });
  });

  bindable?.bind?.('pusher:subscription_error', (error: unknown) => {
    console.error('[notifications/pusher] subscription authentication failure', error);
    reportRealtimeError(nuxtApp, 'authentication_failure', error, { channelName });
  });

  bindable?.bind?.('pusher:error', (error: unknown) => {
    console.error('[notifications/pusher] channel error', error);
    reportRealtimeError(nuxtApp, 'channel_error', error, { channelName });
  });

  bindable?.bind?.('error', (error: unknown) => {
    console.error('[notifications/pusher] channel error', error);
    reportRealtimeError(nuxtApp, 'channel_error', error, { channelName });
  });
}
